import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SlideAnimationContext } from "contexts/SlideAnimation";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { OrganizationProfile, OrganizationVaProfile } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import TemplateEditButton from "components/TemplateEditButton";
import { MAXIMUM_FOLLOWUP_ALLOWED } from "constants/templates";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import EditTemplateDialog from "features/Influencer/Templates/TemplatesTable/EditTemplate";
import { makeDeepCopy } from "utils/updateLocalState";

import CreateTemplateSetUp from "./CreateTemplateSetUp";

const styles = {
  textField: {
    backgroundColor: "white",
    "& .MuiInputBase-root": { backgroundColor: "white" },
    "& .MuiFormHelperText-root": { color: "error.main" },
  },
};

interface Props {
  organizationVaProfile: OrganizationVaProfile | null;
  saveOrgVaProfile: (
    organizationVaProfile: OrganizationVaProfile,
    excludeIncludeFiltersChange?: boolean,
    moveNext?: boolean,
  ) => void;
  organizationProfile: OrganizationProfile | null;
}
const FromScratch = ({
  organizationVaProfile,
  saveOrgVaProfile,
  organizationProfile,
}: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const slideContext = useContext(SlideAnimationContext);
  const { setAlert } = useContext(AlertContext);
  const {
    outreachTemplates,
    followupTemplates,
    editTemplateDialogId,
    setEditTemplateDialogId,
  } = useContext(OutreachTemplatesContext);

  const [defaultOutreach, setDefaultOutreach] = useState<Template | undefined>(
    undefined,
  );
  const [defaultFollowUps, setDefaultFollowUps] = useState<Template[]>([]);

  const defaultTemplate = {
    subject: "",
    contentType: TemplateContentType.OUTREACH,
    body: "",
    name: "Outreach Template",
  };

  const [createTemplateOpen, setCreateTemplateOpen] =
    useState<TemplateContentType | null>(null);
  const [editTemplate, setEditTemplate] = useState<Template>(defaultTemplate);

  const handleBack = async () => {
    if (slideContext) {
      await slideContext.slideOutDown();
      slideContext.slideInDown();
    }
    if (outreachTemplates && outreachTemplates?.length > 0) {
      navigate(`/${routes.nicheSelfServeOnboarding}/${location.search}`);
      return;
    }
    navigate(
      `/${routes.templatesMethodSelfServeOnboarding}/${location.search}`,
    );
  };

  const handleDeleteFollowUp = (index: number) => {
    const updatedFollowUps = makeDeepCopy(defaultFollowUps);
    updatedFollowUps.splice(index, 1);
    setDefaultFollowUps(updatedFollowUps);
  };

  const onOutreachChange = (id: number) => {
    const template = outreachTemplates?.find((t) => t.id === id);
    if (template) {
      setDefaultOutreach(template);
    } else {
      setDefaultOutreach(undefined);
    }
  };

  const onFollowUpChange = (id: number, index: number) => {
    if (id === -1) {
      setCreateTemplateOpen(TemplateContentType.FOLLOWUP);
      setEditTemplate({
        subject: "",
        contentType: TemplateContentType.FOLLOWUP,
        body: "",
        name: `Follow-Up Template ${index + 1}`,
      });
      return;
    }
    const template = followupTemplates?.find((t) => t.id === id);
    setDefaultFollowUps((prevFollowUps) => {
      const updatedFollowUps = [...prevFollowUps];
      if (template) {
        updatedFollowUps[index] = template;
      }
      return updatedFollowUps;
    });
  };

  const handleAddFollowUp = () => {
    if (followupTemplates?.length === 0) {
      setCreateTemplateOpen(TemplateContentType.FOLLOWUP);
      setEditTemplate({
        subject: "",
        contentType: TemplateContentType.FOLLOWUP,
        body: "",
        name: `Follow-Up Template ${defaultFollowUps.length + 1}`,
      });
      return;
    }
    if (defaultFollowUps.length >= MAXIMUM_FOLLOWUP_ALLOWED) {
      return;
    }

    const copy = makeDeepCopy(defaultFollowUps);
    copy.push(followupTemplates[0]);
    setDefaultFollowUps(copy);
  };

  const handleSubmitDefault = async () => {
    if (!defaultOutreach) {
      setAlert("Please select a default outreach template", "warning");
      return;
    }

    const copy = makeDeepCopy(organizationVaProfile || {});
    copy.fallbackTemplateIds = [defaultOutreach.id];
    const followUpIds = defaultFollowUps.map((followUp) => followUp.id);
    if (followUpIds) {
      copy.fallbackTemplateIds.push(...followUpIds);
    }
    saveOrgVaProfile(copy, false, false);
    if (slideContext) {
      await slideContext.slideOutUp();
      slideContext.slideInUp();
    }

    if (
      organizationProfile?.primarySocialMediaPlatform &&
      organizationProfile?.primarySocialMediaPlatform?.length > 0
    ) {
      navigate(`/${routes.faqSelfServeOnboarding}${location.search}`);
      return;
    }
    navigate(
      `/${routes.socialMediaLinksSelfServeOnboarding}${location.search}`,
    );
  };

  const initializeDefaultTemplate = () => {
    if (outreachTemplates?.length > 0 && !defaultOutreach) {
      const defaultOutreachTemplate = outreachTemplates[0];
      setDefaultOutreach(defaultOutreachTemplate);
    }
    if (followupTemplates?.length > 0 && defaultFollowUps.length === 0) {
      const defaultFollowUpTemplate = followupTemplates[0];
      setDefaultFollowUps([defaultFollowUpTemplate]);
    }
  };

  useEffect(() => {
    initializeDefaultTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outreachTemplates?.length, followupTemplates?.length]);

  return (
    <>
      <Typography variant="h3" gutterBottom sx={{ textAlign: "center", mt: 0 }}>
        Pick Template
      </Typography>
      <Typography sx={{ mb: 4 }}>
        Your template should be generic enough to work for{" "}
        <strong>ALL niches</strong>. We use this template for both the niches
        you selected as well as any brands of any niche that you save to be
        pitched to.
      </Typography>

      <Grid container alignItems="center" sx={{ mb: 1 }} gap={2}>
        <Grid item xs={2}>
          <strong>Outreach</strong>
        </Grid>
        <Grid item xs>
          {outreachTemplates?.length > 0 ? (
            <FormControl fullWidth>
              <Select
                name={TemplateContentType.OUTREACH}
                sx={styles.textField}
                value={defaultOutreach?.id || ""}
                onChange={(e: CustomEvent) => onOutreachChange(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>Select Outreach</em>
                </MenuItem>
                {outreachTemplates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Button
              variant="contained"
              disableElevation
              onClick={() =>
                setCreateTemplateOpen(TemplateContentType.OUTREACH)
              }
            >
              Create Template ✨
            </Button>
          )}
        </Grid>

        <Grid item xs={1}>
          {defaultOutreach?.id && (
            <TemplateEditButton templateId={defaultOutreach?.id} />
          )}
        </Grid>
      </Grid>

      {defaultFollowUps?.length > 0 &&
        defaultFollowUps?.map((defaultFollowUp, index) => (
          <Grid container alignItems="center" gap={2} sx={{ mb: 1 }}>
            <Grid item xs={2}>
              <strong>Follow-Up {index + 1}</strong>
            </Grid>
            <Grid item xs>
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  name={TemplateContentType.FOLLOWUP}
                  sx={styles.textField}
                  value={defaultFollowUp?.id || ""}
                  onChange={(e: CustomEvent) =>
                    onFollowUpChange(e.target.value, index)
                  }
                >
                  {followupTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                  <MenuItem value={-1} sx={{ color: "green.main" }}>
                    <em>Create a New Follow-Up </em>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid container item xs={1}>
              {defaultFollowUp?.id && (
                <TemplateEditButton templateId={defaultFollowUp?.id} />
              )}
              {defaultFollowUp?.id && (
                <Tooltip title="Delete follow-up">
                  <IconButton onClick={() => handleDeleteFollowUp(index)}>
                    <Box
                      sx={{ fontSize: 14 }}
                      component="i"
                      className="fa-regular fa-trash"
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        ))}

      {defaultFollowUps?.length < MAXIMUM_FOLLOWUP_ALLOWED && (
        <Grid container alignItems="center" gap={2}>
          <Grid item xs={2}></Grid>
          <Grid item xs>
            <Button
              sx={{ fontSize: 12, textTransform: "none" }}
              size="small"
              onClick={handleAddFollowUp}
              disabled={outreachTemplates?.length === 0}
            >
              Add Follow Up +
            </Button>
          </Grid>
        </Grid>
      )}

      {editTemplateDialogId && (
        <EditTemplateDialog
          propsTemplateId={editTemplateDialogId}
          propsHandleClose={() => {
            setEditTemplateDialogId(null);
          }}
        />
      )}

      <CreateTemplateSetUp
        open={Boolean(createTemplateOpen)}
        handleClose={() => {
          setCreateTemplateOpen(null);
          setEditTemplate(defaultTemplate);
        }}
        editTemplate={editTemplate}
        setEditTemplate={setEditTemplate}
      />

      <Grid item xs="auto" container columnGap={1} sx={{ mt: 4 }}>
        <Grid item xs="auto">
          <Button disableElevation onClick={handleBack}>
            <Box
              component="i"
              className="fa-solid fa-arrow-left"
              sx={{ mr: 1 }}
            />
            Back
          </Button>
        </Grid>
        <Grid item xs></Grid>
        <Grid item xs="auto">
          <Button disableElevation onClick={handleSubmitDefault}>
            Next
            <Box
              sx={{ ml: 1 }}
              component="i"
              className="fa-solid fa-arrow-right"
            />
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default FromScratch;
